import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, ExternalLink, Globe, History, Play, Radar, RefreshCw, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, EmptyState, ErrorState, LoadingState, SeverityBadge, useConfirm } from '@/components/primitives';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { timeAgo } from '@/hooks/useSystemStatus';
import { adminApi, formatUtc, plural, toastError, useAdminQuery, useBusy } from './adminApi';
import { AdminSection, Field, FilterChips, Pager, RefreshError, SearchField, Toolbar } from './AdminUi';

const PAGE = 25;
const KIND_LABEL = { ransomware_victim: 'Ransomware', code_leak: 'Code leak', paste: 'Paste' };
const MATCH_TYPE = { domain: 'Domain', parent_domain: 'Parent domain', email_domain: 'Email domain', name: 'Name' };
const VIEWS = [{ value: 'review', label: 'Review queue' }, { value: 'sources', label: 'Sources' }, { value: 'signals', label: 'Signals' }];
const STATUS_FILTERS = [{ value: 'pending', label: 'Pending' }, { value: 'confirmed', label: 'Confirmed' }, { value: 'dismissed', label: 'Dismissed' }];

/* ------------------------------------------------------------------ review */

function ReviewDialog({ request, onClose, onDone }) {
  const [note, setNote] = useState('');
  const { run, busy } = useBusy();
  useEffect(() => { if (request) setNote(''); }, [request]);
  if (!request) return null;
  const { decision, ids } = request;
  const verb = { confirm: 'Confirm', dismiss: 'Dismiss', reopen: 'Reopen' }[decision];

  const submit = (e) => {
    e.preventDefault();
    run('review', async () => {
      try {
        if (ids.length === 1) await adminApi.post(`/admin/darkweb/matches/${ids[0]}/review`, { decision, note });
        else await adminApi.post('/admin/darkweb/matches/bulk-review', { ids, decision, note });
        toast.success(`${plural(ids.length, 'match', 'matches')} ${decision === 'confirm' ? 'confirmed' : decision === 'dismiss' ? 'dismissed' : 'reopened'}`);
        onDone();
        onClose();
      } catch (err) {
        toastError(err, 'Could not save the review');
      }
    });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="adm-dialog" data-testid="review-dialog">
        <form onSubmit={submit} className="adm-form">
          <DialogHeader>
            <DialogTitle>{verb} {plural(ids.length, 'match', 'matches')}?</DialogTitle>
            <DialogDescription>
              {decision === 'confirm' && 'The customer is notified and sees this finding, including your note.'}
              {decision === 'dismiss' && 'The customer never sees it. Use this for look-alikes, old data and other organizations with the same name.'}
              {decision === 'reopen' && 'It returns to the review queue. A customer who had already seen it keeps seeing it until it is dismissed.'}
            </DialogDescription>
          </DialogHeader>
          <Field label={decision === 'confirm' ? 'Note to the customer (optional)' : 'Reason (optional)'} hint="Up to 500 characters.">
            {(id, hint) => <textarea id={id} aria-describedby={hint} className="adm-input" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-testid="review-note" />}
          </Field>
          <DialogFooter className="adm-dialog-footer">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant={decision === 'dismiss' ? 'destructive' : 'default'} disabled={busy('review')} data-testid="review-submit">
              {busy('review') ? 'Saving…' : verb}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReviewQueue({ onChanged }) {
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [request, setRequest] = useState(null);
  const { data, error, loading, reload } = useAdminQuery(
    () => adminApi.get('/admin/darkweb/matches', { params: { status, q: q.trim() || undefined, limit: PAGE, offset } }).then((r) => r.data),
    [status, q, offset],
  );
  const rows = data?.matches || [];
  const pageIds = rows.map((r) => r.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const done = () => { setSelected(new Set()); reload(); onChanged?.(); };
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePage = () => setSelected((s) => { const n = new Set(s); pageIds.forEach((id) => (allOnPage ? n.delete(id) : n.add(id))); return n; });

  return (
    <>
      <Toolbar>
        <SearchField value={q} onChange={(v) => { setQ(v); setOffset(0); setSelected(new Set()); }} label="Search matches" placeholder="Search organization, victim or domain" />
        <FilterChips label="Status" options={STATUS_FILTERS} value={status} onChange={(v) => { setStatus(v); setOffset(0); setSelected(new Set()); }} testIdPrefix="dw-status" />
      </Toolbar>

      {selected.size > 0 && (
        <div className="adm-bulkbar" role="region" aria-label="Bulk review">
          <span className="adm-strong">{plural(selected.size, 'match', 'matches')} selected</span>
          <span>
            {status !== 'confirmed' && <Button size="sm" onClick={() => setRequest({ decision: 'confirm', ids: [...selected] })}><Check aria-hidden="true" /> Confirm</Button>}
            {status !== 'dismissed' && <Button size="sm" variant="outline" onClick={() => setRequest({ decision: 'dismiss', ids: [...selected] })}><X aria-hidden="true" /> Dismiss</Button>}
          </span>
        </div>
      )}

      {loading ? <LoadingState label="matches" rows={5} />
        : error && !data ? <ErrorState title="Couldn't load matches" error={error} onRetry={reload} />
          : !rows.length ? (
            <EmptyState icon={Radar} title={status === 'pending' ? 'Nothing to review' : `No ${status} matches`}
                        description={status === 'pending' ? 'New matches appear here when a collected signal matches an organization’s domains or names.' : 'Nothing here yet.'} />
          ) : (
            <>
              <RefreshError error={error} onRetry={reload} what="matches" />
              <div className="adm-table-wrap">
                <table className="adm-table adm-table--stack" data-testid="dw-matches-table">
                  <thead>
                    <tr>
                      <th scope="col" className="adm-col-check"><input type="checkbox" className="adm-checkbox" checked={allOnPage} onChange={togglePage} aria-label="Select all matches on this page" /></th>
                      <th scope="col">Organization</th>
                      <th scope="col">Finding</th>
                      <th scope="col">Matched on</th>
                      <th scope="col" className="adm-num">Confidence</th>
                      <th scope="col">Severity</th>
                      <th scope="col"><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((m) => (
                      <tr key={m.id} data-testid="dw-match-row">
                        <td className="adm-col-check"><input type="checkbox" className="adm-checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} aria-label={`Select ${m.evidence.title}`} /></td>
                        <td data-label="Organization" className="adm-break"><div className="adm-strong">{m.organization}</div><div className="adm-meta">{m.industry}</div></td>
                        <td data-label="Finding" className="adm-break">
                          <div className="adm-strong">{m.evidence.title}</div>
                          <div className="adm-meta">
                            <Badge size="sm" plain>{KIND_LABEL[m.kind] || m.kind}</Badge>{' '}
                            {[m.evidence.group, m.evidence.country, m.evidence.sector].filter(Boolean).join(' · ')}
                            {m.evidence.observed_at && <> · <span title={formatUtc(m.evidence.observed_at)}>{timeAgo(m.evidence.observed_at)}</span></>}
                          </div>
                          {m.evidence.excerpt && <pre className="adm-json">{m.evidence.excerpt}</pre>}
                          {m.evidence.reference_url && <a className="adm-link" href={m.evidence.reference_url} target="_blank" rel="noopener noreferrer"><ExternalLink aria-hidden="true" /> Source<span className="sr-only"> (opens in a new tab)</span></a>}
                        </td>
                        <td data-label="Matched on" className="adm-break">
                          {m.matched_on.map((x) => (
                            <div key={x.value}><Badge size="sm" tone="accent" plain>{MATCH_TYPE[x.type] || x.type}</Badge> <span className="adm-mono">{x.value}</span><div className="adm-meta">{x.detail}</div></div>
                          ))}
                        </td>
                        <td data-label="Confidence" className="adm-num">{m.confidence}</td>
                        <td data-label="Severity"><SeverityBadge severity={m.severity} size="sm" short /></td>
                        <td className="adm-actions-cell">
                          {m.status !== 'confirmed' && <Button size="sm" onClick={() => setRequest({ decision: 'confirm', ids: [m.id] })} data-testid="dw-confirm"><Check aria-hidden="true" /> Confirm</Button>}
                          {m.status !== 'dismissed' && <Button size="sm" variant="outline" onClick={() => setRequest({ decision: 'dismiss', ids: [m.id] })} data-testid="dw-dismiss"><X aria-hidden="true" /> Dismiss</Button>}
                          {m.status !== 'pending' && <Button size="sm" variant="ghost" onClick={() => setRequest({ decision: 'reopen', ids: [m.id] })}><RotateCcw aria-hidden="true" /> Reopen</Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager offset={offset} limit={PAGE} total={data.total} onChange={(o) => { setOffset(o); setSelected(new Set()); }} label="Matches pagination" />
            </>
          )}
      <ReviewDialog request={request} onClose={() => setRequest(null)} onDone={done} />
    </>
  );
}

/* ----------------------------------------------------------------- sources */

function SourceState({ s }) {
  if (!s.configured) return <Badge tone="warning" plain size="sm" icon={AlertCircle}>Needs setup</Badge>;
  if (!s.enabled) return <Badge plain size="sm">Disabled</Badge>;
  if (s.last_error) return <Badge tone="danger" plain size="sm" icon={AlertCircle}>Failing</Badge>;
  return <Badge tone="success" plain size="sm" icon={Check}>Active</Badge>;
}

function Sources({ overview, onChanged }) {
  const { run, busy } = useBusy();
  const [confirm, confirmDialog] = useConfirm();
  const sources = overview.data?.sources || [];
  const backfill = overview.data?.backfill;

  const toggle = (s) => run(`toggle-${s.key}`, async () => {
    try { await adminApi.put(`/admin/darkweb/sources/${s.key}`, { enabled: !s.enabled }); await overview.reload(); } catch (err) { toastError(err, 'Could not change the source'); }
  });
  const runNow = (s) => run(`run-${s.key}`, async () => {
    try {
      const res = (await adminApi.post(`/admin/darkweb/sources/${s.key}/run`)).data;
      toast[res.error ? 'error' : 'success'](res.error ? 'The source failed' : `${s.name} checked`, { description: res.error || `${res.collected ?? 0} items collected, ${res.new_signals ?? 0} new, ${res.new_matches ?? 0} new matches.` });
      await overview.reload(); onChanged?.();
    } catch (err) { toastError(err, `Could not run ${s.name}`); }
  });
  const startBackfill = async () => {
    const ok = await confirm({
      title: 'Load the full ransomware history?',
      description: 'Downloads about 33,000 historical listings (about 20 MB) from the public tracker and matches them to every organization. Safe to repeat; known listings are skipped.',
      confirmLabel: 'Load history',
    });
    if (!ok) return;
    run('backfill', async () => {
      try { await adminApi.post('/admin/darkweb/backfill', {}); toast.success('History loading in the background'); setTimeout(overview.reload, 6000); } catch (err) { toastError(err, 'Could not start the backfill'); }
    });
  };
  const rematch = () => run('rematch', async () => {
    try { await adminApi.post('/admin/darkweb/rematch'); toast.success('Re-matching all organizations in the background'); setTimeout(() => { overview.reload(); onChanged?.(); }, 6000); } catch (err) { toastError(err, 'Could not start the re-match'); }
  });

  return (
    <>
      <div className="adm-toolbar">
        <Button size="sm" variant="outline" onClick={startBackfill} disabled={busy('backfill') || backfill?.status === 'running'} data-testid="dw-backfill-btn"><History aria-hidden="true" /> Load ransomware history</Button>
        <Button size="sm" variant="outline" onClick={rematch} disabled={busy('rematch')}><RefreshCw aria-hidden="true" /> Re-match all organizations</Button>
        {backfill && (
          <span className="adm-meta" role="status">
            History: {backfill.status === 'running' ? 'loading…' : backfill.status === 'done' ? `loaded ${backfill.new_signals?.toLocaleString()} new listings, ${backfill.new_matches ?? 0} new matches (${timeAgo(backfill.finished_at)})` : `failed: ${backfill.error}`}
          </span>
        )}
      </div>
      <div className="adm-table-wrap">
        <table className="adm-table adm-table--stack" data-testid="dw-sources-table">
          <thead><tr><th scope="col">Source</th><th scope="col">Status</th><th scope="col">Last run</th><th scope="col" className="adm-num">Signals</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.key} data-testid={`dw-source-${s.key}`}>
                <td data-label="Source" className="adm-break">
                  <div className="adm-strong">{s.name}</div>
                  <div className="adm-meta">{s.description}</div>
                  {!s.configured && <p className="adm-note adm-note--warning" role="note" style={{ marginTop: 6 }}>{s.setup_hint}</p>}
                  {s.last_error && <p className="adm-note adm-note--warning" role="note" style={{ marginTop: 6 }}>{s.last_error}</p>}
                </td>
                <td data-label="Status"><SourceState s={s} /><div className="adm-meta">every {s.interval_minutes} min</div></td>
                <td data-label="Last run" className="adm-mono adm-nowrap adm-secondary">{s.last_run_at ? timeAgo(s.last_run_at) : 'never'}</td>
                <td data-label="Signals" className="adm-num">{s.signals_total.toLocaleString()}</td>
                <td className="adm-actions-cell">
                  <Button size="sm" variant="outline" onClick={() => runNow(s)} disabled={!s.configured || busy(`run-${s.key}`)} data-testid={`dw-run-${s.key}`}><Play aria-hidden="true" /> {busy(`run-${s.key}`) ? 'Running…' : 'Run now'}</Button>
                  <Button size="sm" variant="ghost" onClick={() => toggle(s)} disabled={busy(`toggle-${s.key}`)}>{s.enabled ? 'Disable' : 'Enable'}</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="adm-hint">Phase 1 reads clearnet sources only: nothing here enters Tor, joins forums, buys data or contacts anyone. Stored evidence is a short excerpt with secrets masked, never a full document.</p>
      {confirmDialog}
    </>
  );
}

/* ----------------------------------------------------------------- signals */

function Signals() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const { data, error, loading, reload } = useAdminQuery(
    () => adminApi.get('/admin/darkweb/signals', { params: { q: q.trim() || undefined, limit: PAGE, offset } }).then((r) => r.data),
    [q, offset],
  );
  const rows = data?.signals || [];
  return (
    <>
      <Toolbar><SearchField value={q} onChange={(v) => { setQ(v); setOffset(0); }} label="Search signals" placeholder="Search victim, group or domain" /></Toolbar>
      {loading ? <LoadingState label="signals" rows={5} />
        : error && !data ? <ErrorState title="Couldn't load signals" error={error} onRetry={reload} />
          : !rows.length ? <EmptyState icon={Globe} title="No signals" description={q ? `Nothing matched "${q}".` : 'Run a source or load the ransomware history.'} />
            : (
              <>
                <div className="adm-table-wrap">
                  <table className="adm-table adm-table--stack" data-testid="dw-signals-table">
                    <thead><tr><th scope="col">Listed</th><th scope="col">Kind</th><th scope="col">Title</th><th scope="col">Group</th><th scope="col">Domain</th></tr></thead>
                    <tbody>
                      {rows.map((s) => (
                        <tr key={s.id}>
                          <td data-label="Listed" className="adm-mono adm-nowrap adm-secondary">{formatUtc(s.observed_at)}</td>
                          <td data-label="Kind"><Badge size="sm" plain>{KIND_LABEL[s.kind] || s.kind}</Badge></td>
                          <td data-label="Title" className="adm-break adm-strong">{s.title}</td>
                          <td data-label="Group" className="adm-break">{s.group || '-'}{s.country ? <span className="adm-meta"> · {s.country}</span> : null}</td>
                          <td data-label="Domain" className="adm-mono adm-break">{(s.domains || []).join(', ') || <span className="adm-muted">none</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pager offset={offset} limit={PAGE} total={data.total} onChange={setOffset} label="Signals pagination" />
              </>
            )}
    </>
  );
}

/* -------------------------------------------------------------------- tab */

export default function DarkWebTab({ onChanged }) {
  const [view, setView] = useState('review');
  const overview = useAdminQuery(() => adminApi.get('/admin/darkweb/overview').then((r) => r.data), []);
  const o = overview.data;
  const needSetup = (o?.sources || []).filter((s) => !s.configured).length;
  const views = useMemo(() => VIEWS.map((v) => (v.value === 'review' ? { ...v, count: o?.matches?.pending } : v)), [o]);

  return (
    <AdminSection
      title="Dark web monitoring"
      description="Collected signals are matched to each organization's domains and names. Nothing reaches a customer until an analyst confirms it here."
    >
      {overview.error && !o ? <ErrorState title="Couldn't load dark web status" error={overview.error} onRetry={overview.reload} /> : (
        <div className="adm-toolbar" aria-label="Summary">
          <Badge plain>{o ? o.signals_total.toLocaleString() : '-'} signals</Badge>
          <Badge plain>{o ? o.ransomware_last_24h : '-'} ransomware listings in 24h</Badge>
          <Badge plain tone={o?.matches?.pending ? 'warning' : 'neutral'}>{o ? o.matches.pending : '-'} to review</Badge>
          <Badge plain tone="success">{o ? o.matches.confirmed : '-'} confirmed</Badge>
          {needSetup > 0 && <Badge plain tone="warning" icon={AlertCircle}>{plural(needSetup, 'source')} need setup</Badge>}
        </div>
      )}
      <FilterChips label="View" options={views} value={view} onChange={setView} testIdPrefix="dw-view" />
      {view === 'review' && <ReviewQueue onChanged={() => { overview.reload(); onChanged?.(); }} />}
      {view === 'sources' && <Sources overview={overview} onChanged={onChanged} />}
      {view === 'signals' && <Signals />}
    </AdminSection>
  );
}
